import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import {
  ISupplierAvailabilityException,
  SupplierAvailabilityStateEnum,
} from '../../interfaces/supplier-schedule';
import { chunk, padStart } from 'lodash';
import { SupplierScheduleService } from '../../services/supplier-schedule.service';
import moment from 'moment';

@Component({
  selector: 'supplier-availability-calendar',
  templateUrl: './supplier-availability-calendar.component.html',
  styleUrls: ['./supplier-availability-calendar.component.css'],
})
export class SupplierAvailabilityCalendarComponent implements OnInit {
  public availableImageUrl = 'assets/images/available.png';
  public notAvailableImageUrl = 'assets/images/unavailable.png';

  private _supplierId: number;
  @Input('supplierId')
  set supplierId(value: number) {
    this._supplierId = value;
  }
  get supplierId(): number {
    return this._supplierId;
  }

  private _availabilityExceptions: ISupplierAvailabilityException[];
  @Input('availabilityExceptions')
  set availabilityExceptions(value: ISupplierAvailabilityException[]) {
    this._availabilityExceptions = value;
  }
  get availabilityExceptions(): ISupplierAvailabilityException[] {
    return this._availabilityExceptions;
  }

  @Input('dayData') dayData: string[];

  public startDate: Date = null;

  // data for the two months shown in the calendar (re-populated when current month is changed)
  // includes a translation of recurring schedule as well as exceptions
  public scheduleData: {
    month: number; // offset from startDate month (i.e. 0 = current start month)
    monthName: string;
    year: number;
    dayData: {
      day: string;
      weekday: number; // weekday
      date: Date;
      formattedDate: string;
      availabilityState: SupplierAvailabilityStateEnum;
      inactive: boolean; // true when day is before today's date
      isOtherMonth: boolean; // true when date is from previous or next month in this months array (calendar padding)
    }[][];
  }[];

  @Output() availabilityExceptionsChanged: EventEmitter<ISupplierAvailabilityException[]>;

  constructor(private scheduleService: SupplierScheduleService) {
    this.availabilityExceptionsChanged = new EventEmitter<ISupplierAvailabilityException[]>();
  }

  ngOnInit() {
    // ensure start date time is midday to avoid problems when saving dates with daylight savings
    // i.e. if it starts at midnight then the c# service will translate it to the previous/next day.
    this.startDate = moment().startOf('month').hour(12).toDate();

    // translate recurring schedule into array matching days in each month
    this.setDateInfo();
  }

  setDateInfo() {
    this.scheduleData = Array(2)
      .fill(1)
      .map((y, m) => ({
        month: m,
        monthName: this.getMomentMonth(m).toDate().toLocaleString('default', { month: 'long' }),
        year: this.getMomentMonth(m).year(),
        dayData: this.getDaysArray(m),
      }));
  }

  getDaysArray(m: number) {
    var daysInMonth = this.getMomentMonth(m).daysInMonth();

    var temp = Array(daysInMonth)
      .fill(1)
      .map((x, i) => ({
        day: this.getDayString(m, i),
        weekday: this.getMomentDate(m, i).day(),
        date: this.getMomentDate(m, i).toDate(),
        formattedDate: moment(this.getMomentDate(m, i)).format("ddd DD MMM 'YY"),
        availabilityState: this.scheduleService.getDayAvailabilityException(
          this.startDate,
          m,
          i,
          this.availabilityExceptions,
        )?.availabilityState,
        inactive: this.getMomentDate(m, i).isBefore(moment.now(), 'day'),
        isOtherMonth: false,
      }));

    // pad the array at start and end with weekdays not included in month
    this.padPreDays(temp);
    this.padPostDays(temp);

    let t1: any = temp.splice(0, 1)[0];
    temp.push(t1);

    var chunkedArray = chunk(temp, 7);

    return chunkedArray;
  }

  padPreDays(data) {
    var i;
    var firstDay = data[0].weekday;
    let preDate = moment(data[0].date);

    // add days from previous month
    for (i = 0; i < firstDay; i++) {
      preDate.subtract(1, 'd').toDate();
      data.unshift({
        day: moment(preDate).date(),
        weekday: moment(preDate).day(),
        date: preDate.toDate(),
        available: null,
        inactive: true,
        isOtherMonth: true,
      });
    }
  }

  padPostDays(data) {
    var i;
    var lastDay = data[data.length - 1].weekday;
    let postDate = moment(data[data.length - 1].date);

    // add days from beginning of next month
    for (i = lastDay; i < 6; i++) {
      postDate.add(1, 'd').toDate();
      data.push({
        day: moment(postDate).date(),
        weekday: moment(postDate).day(),
        date: postDate.toDate(),
        available: null,
        inactive: true,
        isOtherMonth: true,
      });
    }
  }

  getDayString(monthAdd: number, dayAdd: number) {
    var day = this.getMomentDate(monthAdd, dayAdd).date().toString();
    var dayString = padStart(day, 2, ' ');

    return dayString;
  }

  getMomentMonth(monthAdd: number) {
    return moment(this.startDate).add(monthAdd, 'M');
  }
  getMomentDate(monthAdd: number, dayAdd: number) {
    return moment(this.startDate).add(monthAdd, 'M').add(dayAdd, 'd');
  }

  changeAvailabilityException(id: number, exceptionDate: Date) {
    // save availability and emit event for exceptions calendar
    this.availabilityExceptionsChanged.emit(this.availabilityExceptions);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.startDate != null) this.setDateInfo();
  }

  dayClicked(day) {
    let except = this.scheduleService.getExceptionByDate(day.date, this.availabilityExceptions);

    console.log(except);

    if (except) {
      console.log('exception found');
      // if the exception is available, switch to unavailable
      // if unavailable then remove exception (returns to indeterminate)
      if (except.availabilityState == SupplierAvailabilityStateEnum.available) {
        console.log('hit 1');
        except.availabilityState = SupplierAvailabilityStateEnum.unavailable;
      } else if (except.availabilityState == SupplierAvailabilityStateEnum.doNotDisturb) {
        // Do Nothing - DND
      } else {
        console.log('hit 3');
        let index = this.scheduleService.getExceptionIndexByDate(
          day.date,
          this.availabilityExceptions,
        );
        this.availabilityExceptions.splice(index, 1);
      }
    } else {
      let newExcept: ISupplierAvailabilityException = <ISupplierAvailabilityException>{
        id: -1,
        availabilityState: SupplierAvailabilityStateEnum.available,
        exceptionDate: day.date,
      };
      this.availabilityExceptions.push(newExcept);
    }

    //console.log("exception: ", except, "   - current exceptions: ", this.availabilityExceptions);

    this.setDateInfo();
  }

  updateAllExceptions() {
    if (this.availabilityExceptions == null) return null;

    // this method is called when the recurring schedule is changed in order to remove exceptions that are no longer required
    for (let i = this.availabilityExceptions.length - 1; i >= 0; i--) {
      let weekday = moment(this.availabilityExceptions[i].exceptionDate).day();
      // let available = this.scheduleService.getWeekDayAvailability(weekday, this.availability);
      //
      // if (this.availabilityExceptions[i].available == available) {
      //     this.availabilityExceptions.splice(i, 1);
      // }
    }
  }

  prevMonthClicked() {
    let m = moment();

    let month = this.startDate.getMonth();
    let year = this.startDate.getFullYear();

    let currentMonth = m.month();
    let currentYear = m.year();

    if (month > currentMonth || currentYear < year) {
      this.startDate = moment(this.startDate).subtract(1, 'M').toDate();
      this.setDateInfo();
    }
  }

  nextMonthClicked() {
    this.startDate = moment(this.startDate).add(1, 'M').toDate();
    this.setDateInfo();
  }

  getAvailabilityTooltip(day: {
    day: string;
    weekday: number;
    date: Date;
    formattedDate: string;
    availabilityState: SupplierAvailabilityStateEnum | undefined;
    inactive: boolean;
    isOtherMonth: boolean;
  }) {
    if (day.inactive) return '';

    let str = `<div class='x-tooltip-date'> ${day.formattedDate}</div>`;

    if (!day.inactive && day.availabilityState == SupplierAvailabilityStateEnum.available) {
      str += `Supplier is available`;
    } else if (
      !day.inactive &&
      day.availabilityState == SupplierAvailabilityStateEnum.unavailable
    ) {
      str += `Supplier is unavailable`;
    } else if (
      !day.inactive &&
      day.availabilityState == SupplierAvailabilityStateEnum.doNotDisturb
    ) {
      str += `Supplier is Do Not Disturb`;
    } else if (!day.inactive && day.availabilityState == null) {
      str += `Supplier has not set their availability`;
    }

    str += "<div class='xtooltip-small'>Click to change</div>";
    return str;
  }

  protected readonly SupplierAvailabilityStateEnum = SupplierAvailabilityStateEnum;
}
